'use client';

/**
 * Live insets for the "teacher-workspace" reference screen.
 * OWNED BY THE INSETS STAGE — see ../index.tsx for the contract.
 *
 * Covers (reference pixels, 1672×941):
 *   - "Recommended Action" list [1063,255,248,300]: four radio rows (teacher-action-N);
 *   - "Adjust this action" rows [1063,660,250,100]: scaffold / grouping / resource from a fixed table;
 *   - "Apply Action" [1062,779,250,35] → navigate('technology');
 *   - route node text [1455,552,190,60]: action in progress + resource.
 * The painted route curve, the fifth (unselectable) option and the evidence trail stay as painted.
 */

import { useContext, useEffect, useRef, useState, type CSSProperties } from 'react';
import { LivingContext } from '../../context';
import { SCENE_CANVAS, rectStyle, type Bilingual, type Rect } from '../../types';
import type { InsetProps } from '../index';
import styles from './teacher-workspace.module.css';

const CANVAS = SCENE_CANVAS['teacher-workspace'];
const at = (rect: Rect): CSSProperties => rectStyle(rect, CANVAS);
const stagger = (index: number): CSSProperties => ({ '--i': Math.min(index, 5) } as CSSProperties);

/** 4 px wider than the painted list on each side so the selected card's painted border never peeks out. */
const LIST: Rect = [1059, 255, 256, 300];
const ADJUST: Rect = [1063, 660, 250, 100];
const APPLY: Rect = [1062, 779, 250, 35];
const ROUTE_NODE: Rect = [1455, 552, 190, 60];
/** Row tops inside the list, reference pixels (matches hotspots.ts actionY). */
const ROW_TOP = [255, 326, 407, 480] as const;
const ROW_HEIGHT = 69;

interface TeacherAction {
  title: Bilingual;
  sub: Bilingual;
  /** 1..3 filled segments. */
  scaffold: 1 | 2 | 3;
  grouping: Bilingual;
  resource: Bilingual;
  icon: 'book' | 'compass' | 'grid' | 'people';
}

const ACTIONS: readonly TeacherAction[] = [
  {
    title: { en: 'Mini-lesson', ru: 'Мини-урок' },
    sub: { en: 'Introduce with a short targeted lesson', ru: 'Короткое адресное объяснение' },
    scaffold: 3,
    grouping: { en: 'Whole class', ru: 'Весь класс' },
    resource: { en: 'Short lesson', ru: 'Короткий урок' },
    icon: 'book',
  },
  {
    title: { en: 'Guided Exploration', ru: 'Поиск с опорой' },
    sub: { en: 'Explore and make sense with support', ru: 'Ученики исследуют и осмысляют с опорой' },
    scaffold: 2,
    grouping: { en: 'Pairs', ru: 'Пары' },
    resource: { en: 'Interactive Lab', ru: 'Лаборатория' },
    icon: 'compass',
  },
  {
    title: { en: 'Practice Set', ru: 'Набор практики' },
    sub: { en: 'Build fluency with focused practice', ru: 'Беглость через фокусную практику' },
    scaffold: 1,
    grouping: { en: 'Individual', ru: 'Индивидуально' },
    resource: { en: 'Practice tasks', ru: 'Набор заданий' },
    icon: 'grid',
  },
  {
    title: { en: 'Peer Teach', ru: 'Объяснение в паре' },
    sub: { en: 'A peer explains and the pair discusses', ru: 'Объяснение от сверстника' },
    scaffold: 2,
    grouping: { en: 'Pairs', ru: 'Пары' },
    resource: { en: 'Discussion guide', ru: 'План обсуждения' },
    icon: 'people',
  },
];

const SCAFFOLD: Record<1 | 2 | 3, Bilingual> = {
  1: { en: 'Low', ru: 'Низкая' },
  2: { en: 'Medium', ru: 'Средняя' },
  3: { en: 'High', ru: 'Высокая' },
};

function useLiveFlag(key: string): boolean {
  const first = useRef(key);
  const [live, setLive] = useState(false);
  useEffect(() => {
    if (!live && key !== first.current) setLive(true);
  }, [key, live]);
  return live;
}

function Icon({ kind }: { kind: TeacherAction['icon'] | 'check' | 'people-small' | 'resource' }) {
  switch (kind) {
    case 'book':
      return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 5.5A2.5 2.5 0 0 1 5.5 3H11v16H5.5A2.5 2.5 0 0 0 3 21zM21 5.5A2.5 2.5 0 0 0 18.5 3H13v16h5.5a2.5 2.5 0 0 1 2.5 2z" /></svg>;
    case 'compass':
      return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M15.5 8.5l-2 5-5 2 2-5z" /></svg>;
    case 'grid':
      return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 6v12M6 12h12M8.5 8.5l7 7M15.5 8.5l-7 7" /></svg>;
    case 'people':
    case 'people-small':
      return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="9" cy="8" r="3.2" /><path d="M3 19c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5M15.5 5.5a3 3 0 0 1 0 5.6M17 13.7c2.4.5 4 2.3 4 5.3" /></svg>;
    case 'resource':
      return <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="4" y="4" width="16" height="16" rx="3" /><path d="M9 12h6M12 9v6" /></svg>;
    case 'check':
      return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 12.5l4 4L18 8" /></svg>;
  }
}

export default function TeacherWorkspaceInsets({ lang, state, update, navigate, spotlight, onInteraction }: InsetProps) {
  const living = useContext(LivingContext);
  const reduced = living?.reducedMotion ?? false;
  const ru = lang === 'ru';
  const selected = Math.max(0, Math.min(ACTIONS.length - 1, state.teacherAction));
  const live = useLiveFlag(String(selected));
  const action = ACTIONS[selected];
  const pick = (copy: Bilingual) => (ru ? copy.ru : copy.en);

  const labels = {
    list: ru ? 'Рекомендуемое действие' : 'Recommended action',
    adjust: ru ? 'Настройка действия' : 'Adjust this action',
    scaffold: ru ? 'Уровень опоры' : 'Scaffold Level',
    grouping: ru ? 'Формат' : 'Grouping',
    resource: ru ? 'Ресурс' : 'Resource',
    apply: ru ? 'Применить действие' : 'Apply Action',
    applyName: ru ? 'Применить действие и перейти к архитектуре' : 'Apply action and continue to the architecture',
    inProgress: ru ? 'Действие в работе' : 'Action in Progress',
    routeNode: ru ? 'Узел маршрута: действие в работе' : 'Route node: action in progress',
  };

  const press = (label: string, fn: () => void) => () => {
    onInteraction?.(label);
    fn();
  };

  return (
    <div className={styles.root} data-live={live ? 'true' : undefined} data-reduced={reduced ? 'true' : undefined}>
      <div className={styles.list} style={at(LIST)} role="radiogroup" aria-label={labels.list}>
        {ACTIONS.map((item, index) => {
          const id = `teacher-action-${index}`;
          const active = index === selected;
          const name = `${ru ? 'Вмешательство' : 'Intervention'} ${index + 1}: ${pick(item.title)}`;
          return (
            <button
              key={id}
              type="button"
              role="radio"
              className={`${styles.row} ${active ? styles.rowActive : ''}`}
              style={{ top: `${((ROW_TOP[index] - LIST[1]) / LIST[3]) * 100}%`, height: `${(ROW_HEIGHT / LIST[3]) * 100}%` }}
              data-hotspot={id}
              data-spotlight={spotlight === id ? 'true' : undefined}
              aria-checked={active}
              aria-label={name}
              onClick={press(name, () => update({ teacherAction: index }))}
            >
              <span className={styles.rowIcon}><Icon kind={item.icon} /></span>
              <span className={styles.rowText}>
                <strong>{pick(item.title)}</strong>
                <small>{pick(item.sub)}</small>
              </span>
              <span className={styles.radio}>{active && <Icon kind="check" />}</span>
              {spotlight === id && <i className={styles.pulse} aria-hidden="true" />}
            </button>
          );
        })}
        {live && <i key={`relight-${selected}`} className={styles.relight} aria-hidden="true" />}
      </div>

      <dl className={styles.adjust} style={at(ADJUST)} aria-label={labels.adjust}>
        <div key={`s-${selected}`} className={styles.adjustRow} style={stagger(0)}>
          <dt>{labels.scaffold}</dt>
          <dd>
            <span className={styles.segments} aria-hidden="true">
              {[1, 2, 3].map((n) => <i key={n} className={n <= action.scaffold ? styles.segmentOn : ''} />)}
            </span>
            <span>{pick(SCAFFOLD[action.scaffold])}</span>
          </dd>
        </div>
        <div key={`g-${selected}`} className={styles.adjustRow} style={stagger(1)}>
          <dt>{labels.grouping}</dt>
          <dd><span className={styles.adjustIcon}><Icon kind="people-small" /></span><span>{pick(action.grouping)}</span></dd>
        </div>
        <div key={`r-${selected}`} className={styles.adjustRow} style={stagger(2)}>
          <dt>{labels.resource}</dt>
          <dd><span className={styles.adjustIcon}><Icon kind="resource" /></span><span>{pick(action.resource)}</span></dd>
        </div>
      </dl>

      <button
        type="button"
        className={styles.apply}
        style={at(APPLY)}
        data-hotspot="workspace-apply"
        data-spotlight={spotlight === 'workspace-apply' ? 'true' : undefined}
        aria-label={labels.applyName}
        onClick={press(labels.applyName, () => navigate('technology'))}
      >
        {labels.apply}
        {spotlight === 'workspace-apply' && <i className={styles.pulse} aria-hidden="true" />}
      </button>

      <div className={styles.routeNode} style={at(ROUTE_NODE)} role="img" aria-label={`${labels.routeNode}: ${pick(action.title)}, ${pick(action.resource)}`}>
        <div key={`route-${selected}`} className={styles.routeBody}>
          <strong style={stagger(0)}>{labels.inProgress}</strong>
          <span style={stagger(1)}>{pick(action.title)}</span>
          <span style={stagger(2)}>{pick(action.resource)}</span>
        </div>
      </div>
    </div>
  );
}
